import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface SessionContextType {
  session: Session | null;
  loading: boolean;
  previouslyHadSession: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [previouslyHadSession, setPreviouslyHadSession] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const mounted = useRef(true);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const maxRetries = 3;
  const refreshTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    mounted.current = true;
    let retryCount = 0;
    let authSubscription: { unsubscribe: () => void } | null = null;

    const initSession = async (retry = false) => {
      try {
        if (retry) {
          setIsRefreshing(true);
        }

        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (mounted.current) {
          setSession(currentSession);
          if (currentSession) setPreviouslyHadSession(true);
          setLoading(false);
        }

        // Set up auth subscription
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
          if (!mounted.current) return;

          if (event === 'SIGNED_OUT') {
            setSession(null);
            setPreviouslyHadSession(false);
          } else if (newSession) {
            setSession(newSession);
            setPreviouslyHadSession(true);
          }
          setLoading(false);
        });

        authSubscription = subscription;

      } catch (error) {
        console.error('Session initialization error:', error);
        
        // Retry on network errors with exponential backoff
        if (error.message?.includes('Failed to fetch') && retryCount < maxRetries) {
          retryCount++;
          const delay = Math.min(1000 * Math.pow(2, retryCount), 8000);
          
          retryTimeoutRef.current = setTimeout(() => {
            if (mounted.current) initSession();
          }, delay);
          
          return;
        }

        if (mounted.current) {
          setSession(null);
          setLoading(false);
        }
        if (retry && mounted.current) {
          setIsRefreshing(false);
        }
      }
    };

    initSession();

    // Set up session refresh interval
    const scheduleRefresh = () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Schedule next refresh 4 minutes before token expires
      const expiresIn = session?.expires_in ?? 3600;
      const refreshDelay = Math.max(0, (expiresIn - 240) * 1000);
      
      refreshTimeoutRef.current = setTimeout(() => {
        if (mounted.current && session) {
          initSession(true);
        }
      }, refreshDelay);
    };

    // Schedule initial refresh if we have a session
    if (session) {
      scheduleRefresh();
      }

    return () => {
      mounted.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [session?.expires_in]);

  return (
    <SessionContext.Provider value={{ session, loading, previouslyHadSession }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
}